import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  increment,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FILM_TIERS, SUBSCRIPTION_STATUS } from '../filmSubscriptionTiers';

const ENTERPRISE_COMPANY_COLLECTION = 'film_companies'; // Legacy collection name kept for compatibility

/**
 * Enterprise Company Service
 * Handles enterprise company operations with Firebase
 */

/**
 * Get company by ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Company data
 */
export const getCompanyById = async (companyId) => {
  try {
    const companyDoc = await getDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId));
    
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }
    
    return {
      id: companyDoc.id,
      ...companyDoc.data()
    };
  } catch (error) {
    console.error('Error fetching company:', error);
    throw error;
  }
};

/**
 * Get user's company
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Company data or null
 */
export const getUserCompany = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    
    const companyId = userData.enterpriseCompanyId || userData.filmCompanyId;

    if (!companyId) {
      return null;
    }
    
    return await getCompanyById(companyId);
  } catch (error) {
    console.error('Error fetching user company:', error);
    throw error;
  }
};

/**
 * Create a new enterprise company
 * @param {Object} companyData - Company data
 * @param {string} createdBy - User ID of creator (super admin)
 * @returns {Promise<string>} Company ID
 */
export const createCompany = async (companyData, createdBy) => {
  try {
    const companyRef = doc(collection(db, ENTERPRISE_COMPANY_COLLECTION));
    
    const newCompany = {
      companyId: companyRef.id,
      companyName: companyData.companyName,
      subscriptionTier: FILM_TIERS.ENTERPRISE,
      subscriptionStatus: companyData.subscriptionStatus || SUBSCRIPTION_STATUS.ACTIVE,
      subscriptionExpiresAt: companyData.subscriptionExpiresAt || null,
      subscriptionStartedAt: new Date(),
      totalSeats: companyData.totalSeats || 10,
      usedSeats: 0,
      enterpriseAdminUids: companyData.enterpriseAdminUids || [],
      createdAt: new Date(),
      createdBy: createdBy,
      stripeCustomerId: companyData.stripeCustomerId || null,
      stripeSubscriptionId: companyData.stripeSubscriptionId || null,
      billingEmail: companyData.billingEmail,
      isActive: true
    };
    
    await setDoc(companyRef, newCompany);
    
    console.log(`✅ Created company ${newCompany.companyName} with ID ${companyRef.id}`);
    return companyRef.id;
  } catch (error) {
    console.error('Error creating company:', error);
    throw error;
  }
};

/**
 * Update company details
 * @param {string} companyId - Company ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateCompany = async (companyId, updates) => {
  try {
    await updateDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId), updates);
    console.log(`✅ Updated company ${companyId}`);
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

/**
 * Invite user to company
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID to invite
 * @param {string} invitedBy - User ID of inviter
 * @returns {Promise<void>}
 */
export const inviteUserToCompany = async (companyId, userId, invitedBy) => {
  try {
    // Get company to check seat availability
    const company = await getCompanyById(companyId);
    
    if (company.usedSeats >= company.totalSeats) {
      throw new Error('No available seats in this company');
    }
    
    // Update user document
    await updateDoc(doc(db, 'users', userId), {
      enterpriseCompanyId: companyId,
      filmCompanyId: companyId,
      filmSubscriptionTier: FILM_TIERS.ENTERPRISE,
      filmSubscriptionStatus: company.subscriptionStatus,
      filmSubscriptionExpiresAt: company.subscriptionExpiresAt,
      addedToEnterpriseCompanyAt: new Date(),
      addedToFilmCompanyAt: new Date(), // legacy field
      addedBy: invitedBy
    });
    
    // Increment used seats
    await updateDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId), {
      usedSeats: increment(1)
    });
    
    console.log(`✅ Added user ${userId} to company ${companyId}`);
  } catch (error) {
    console.error('Error inviting user to company:', error);
    throw error;
  }
};

/**
 * Remove user from company
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID to remove
 * @returns {Promise<void>}
 */
export const removeUserFromCompany = async (companyId, userId) => {
  try {
    // Update user document - remove company association and downgrade to free
    await updateDoc(doc(db, 'users', userId), {
      enterpriseCompanyId: null,
      filmCompanyId: null,
      filmSubscriptionTier: FILM_TIERS.FREE,
      filmSubscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
      isEnterpriseAdmin: false,
      isFilmEnterpriseAdmin: false,
      addedToEnterpriseCompanyAt: null,
      addedToFilmCompanyAt: null,
      addedBy: null
    });
    
    // Decrement used seats and remove from admin list if present
    const company = await getCompanyById(companyId);
    const updates = {
      usedSeats: increment(-1)
    };
    
    // Remove from enterprise admin list if present
    if (company.enterpriseAdminUids.includes(userId)) {
      updates.enterpriseAdminUids = arrayRemove(userId);
    }
    
    await updateDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId), updates);
    
    console.log(`✅ Removed user ${userId} from company ${companyId}`);
  } catch (error) {
    console.error('Error removing user from company:', error);
    throw error;
  }
};

/**
 * Find user by email
 * @param {string} email - User email
 * @returns {Promise<Object|null>} User data or null if not found
 */
export const findUserByEmail = async (email) => {
  try {
    const usersCollection = collection(db, 'users');
    const snapshot = await getDocs(
      query(usersCollection, where('email', '==', email.toLowerCase().trim()))
    );

    if (snapshot.empty) {
      return null;
    }

    const userDoc = snapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error finding user by email:', error);
    throw error;
  }
};

/**
 * Get all users in a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} List of users
 */
export const getCompanyUsers = async (companyId) => {
  try {
    const usersCollection = collection(db, 'users');

    const collectUsers = async (field) => {
      const snapshot = await getDocs(
        query(usersCollection, where(field, '==', companyId))
      );

      return snapshot.docs.map((userDoc) => ({
        id: userDoc.id,
        ...userDoc.data(),
      }));
    };

    let users = await collectUsers('enterpriseCompanyId');

    if (users.length === 0) {
      users = await collectUsers('filmCompanyId');
    }

    return users;
  } catch (error) {
    console.error('Error fetching company users:', error);
    throw error;
  }
};

/**
 * Add enterprise admin to company
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID to make admin
 * @returns {Promise<void>}
 */
export const addEnterpriseAdmin = async (companyId, userId) => {
  try {
    // Update company
    await updateDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId), {
      enterpriseAdminUids: arrayUnion(userId)
    });
    
    // Update user
    await updateDoc(doc(db, 'users', userId), {
      isEnterpriseAdmin: true,
      isFilmEnterpriseAdmin: true
    });
    
    console.log(`✅ Added ${userId} as enterprise admin for company ${companyId}`);
  } catch (error) {
    console.error('Error adding enterprise admin:', error);
    throw error;
  }
};

/**
 * Remove enterprise admin from company
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID to remove as admin
 * @returns {Promise<void>}
 */
export const removeEnterpriseAdmin = async (companyId, userId) => {
  try {
    // Update company
    await updateDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId), {
      enterpriseAdminUids: arrayRemove(userId)
    });
    
    // Update user
    await updateDoc(doc(db, 'users', userId), {
      isEnterpriseAdmin: false,
      isFilmEnterpriseAdmin: false
    });
    
    console.log(`✅ Removed ${userId} as enterprise admin for company ${companyId}`);
  } catch (error) {
    console.error('Error removing enterprise admin:', error);
    throw error;
  }
};

/**
 * Get all Enterprise companies
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} List of companies
 */
export const getAllCompanies = async (filters = {}) => {
  try {
    let q = query(collection(db, ENTERPRISE_COMPANY_COLLECTION));
    
    if (filters.status) {
      q = query(q, where('subscriptionStatus', '==', filters.status));
    }
    
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    const snapshot = await getDocs(q);
    const companies = [];
    
    snapshot.forEach(doc => {
      companies.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return companies;
  } catch (error) {
    console.error('Error fetching companies:', error);
    
    // Provide more helpful error messages
    if (error.code === 'permission-denied' || error.message?.includes('permission')) {
      const permissionError = new Error(
        'You do not have permission to access enterprise companies. Please ensure you are logged in as an administrator.'
      );
      permissionError.code = 'permission-denied';
      permissionError.originalError = error;
      throw permissionError;
    }
    
    throw error;
  }
};

/**
 * Update company seat count
 * @param {string} companyId - Company ID
 * @param {number} totalSeats - New total seats
 * @returns {Promise<void>}
 */
export const updateCompanySeats = async (companyId, totalSeats) => {
  try {
    const company = await getCompanyById(companyId);
    
    if (totalSeats < company.usedSeats) {
      throw new Error(`Cannot reduce seats below current usage (${company.usedSeats} seats in use)`);
    }
    
    await updateDoc(doc(db, ENTERPRISE_COMPANY_COLLECTION, companyId), {
      totalSeats: totalSeats
    });
    
    console.log(`✅ Updated company ${companyId} to ${totalSeats} seats`);
  } catch (error) {
    console.error('Error updating company seats:', error);
    throw error;
  }
};

const enterpriseCompanyService = {
  getCompanyById,
  getUserCompany,
  createCompany,
  updateCompany,
  inviteUserToCompany,
  removeUserFromCompany,
  findUserByEmail,
  getCompanyUsers,
  addEnterpriseAdmin,
  removeEnterpriseAdmin,
  getAllCompanies,
  updateCompanySeats
};

export default enterpriseCompanyService;

